import { ReactElement } from "react";
import { Anchor } from "@twilio-paste/core/anchor";
import { Box } from "@twilio-paste/core/box";
import { Heading } from "@twilio-paste/core/heading";
import { Text } from "@twilio-paste/core/text";
import { ArtificialIntelligenceIcon } from "@twilio-paste/icons/esm/ArtificialIntelligenceIcon";

import {
  OscPreChatHeaderProps,
  WiseOwlIngress,
} from "@twiliointernal/e2-commons-shared-definitions";
import {
  oneAdminPreChatConfiguration,
  oscPreChatConfiguration,
  OscPreChatHeaderUiProps,
  sghcPreChatConfiguration,
  wozdPreChatConfiguration,
  wiseOwlAppPreChatConfiguration,
  consolePreChatConfiguration,
} from "../../definitions";
import styles from "./OscPreChatHeader.module.css";
import { useWiseOwlContext } from "../../../../hooks";

const OscPreChatHeaderUI = ({
  displayName,
  ingress,
  isOneAdmin,
}: OscPreChatHeaderUiProps): ReactElement => {
  const { user } = useWiseOwlContext();
  const getPreChatConfiguration = (): OscPreChatHeaderProps => {
    // One Admin ingress is not supported yet
    if (isOneAdmin) {
      return oneAdminPreChatConfiguration;
    }

    switch (ingress) {
      case WiseOwlIngress.zendesk: {
        return wozdPreChatConfiguration(user?.firstName);
      }
      case WiseOwlIngress.wiseOwlApp: {
        return wiseOwlAppPreChatConfiguration(user?.firstName);
      }
      case WiseOwlIngress.sendGrid: {
        return sghcPreChatConfiguration;
      }
      case WiseOwlIngress.console: {
        return consolePreChatConfiguration(displayName);
      }
      case WiseOwlIngress.oscUngated: {
        return oscPreChatConfiguration();
      }
      // Default to OSC
      default: {
        return oscPreChatConfiguration(user?.firstName);
      }
    }
  };

  const renderPreChatHeader = ({
    headerText,
    subText,
    anchorProps,
    secondaryHeaderText,
  }: OscPreChatHeaderProps): ReactElement => (
    <Box as="div" className={styles.container}>
      <Box as="div" className={styles.icon}>
        <ArtificialIntelligenceIcon decorative size="sizeIcon100" />
      </Box>
      <Heading as="h1" variant="heading20" marginBottom="space0">
        {headerText}
      </Heading>
      {secondaryHeaderText && (
        <Box as="div" className={styles.secondaryHeader}>
          <Heading as="h1" variant="heading20">
            {secondaryHeaderText}
          </Heading>
        </Box>
      )}
      <Text as="p" className={styles.subtext}>
        {subText}

        {anchorProps && (
          <Anchor
            href={anchorProps.href}
            onClick={anchorProps.onClick}
            showExternal={anchorProps.showExternal}
            marginLeft="space20"
          >
            {anchorProps.children}
          </Anchor>
        )}
      </Text>
    </Box>
  );

  const preChatConfiguration = getPreChatConfiguration();

  return renderPreChatHeader(preChatConfiguration);
};

export default OscPreChatHeaderUI;
